import { useState, type FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { login } from "../api/auth";
import { ApiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const [loginValue, setLoginValue] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { setUser } = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState | null)?.from ?? "/";

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const user = await login(loginValue.trim(), password);
      setUser(user);
      navigate(user.password_must_change ? "/change-password" : from, {
        replace: true,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "INVALID_CREDENTIALS") {
          setError("Неверный логин или пароль");
        } else if (err.code === "ACCOUNT_LOCKED") {
          setError("Слишком много попыток. Попробуйте позже.");
        } else if (err.code === "ACCOUNT_INACTIVE") {
          setError("Аккаунт деактивирован");
        } else if (err.status === 429) {
          setError("Слишком частые попытки. Подождите минуту.");
        } else {
          setError("Не удалось войти");
        }
      } else {
        setError("Сеть недоступна");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center px-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm space-y-4 rounded-lg bg-white p-6 shadow"
      >
        <header>
          <h1 className="text-xl font-semibold text-slate-900">
            Петрополь Академия
          </h1>
          <p className="text-sm text-slate-500">Вход в кабинет</p>
        </header>

        <div className="space-y-1">
          <label htmlFor="login" className="block text-sm font-medium text-slate-700">
            Логин
          </label>
          <input
            id="login"
            name="login"
            type="text"
            autoComplete="username"
            autoFocus
            required
            value={loginValue}
            onChange={(e) => setLoginValue(e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="password" className="block text-sm font-medium text-slate-700">
            Пароль
          </label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
          />
        </div>

        {error && (
          <div role="alert" className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full btn-primary"
        >
          {submitting ? "Вход…" : "Войти"}
        </button>

        <p className="text-xs text-slate-500">
          Логин и пароль выдаёт администратор Академии.
        </p>
      </form>
    </div>
  );
}
